import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useToast } from '../ui/use-toast';

/** One page of a grid's tiles, and whether there is another after it. */
export interface TilePage<T> {
  items: T[];
  hasMore: boolean;
}

interface PagedTileGridProps<T> {
  /** Asks for one page, counted from 1. A new function starts the grid over. */
  fetchPage: (page: number) => Promise<TilePage<T>>;
  renderTile: (item: T) => ReactNode;
  itemKey: (item: T) => string;
  emptyText: string;
  errorText: string;
  /** Names the grid, its loading state (`-loading`) and its sentinel (`-sentinel`) for the tests. */
  testId: string;
  className?: string;
}

/**
 * Tiles to a row: three on a phone, four on a tablet, five on a desktop — the
 * grid is read as photos, and a photo wants to stay about a thumb wide.
 * `md` and `lg` are the tablet and desktop cut-offs the rest of the app uses.
 */
const GRID = 'grid grid-cols-3 gap-0.5 md:grid-cols-4 lg:grid-cols-5';

/** A first screen of grey squares: whole rows on a phone and a tablet, two and a bit on a desktop. */
const FIRST_SKELETON = 12;

/** One row's worth on a phone while the next page is on its way. */
const MORE_SKELETON = 3;

/** How far below the fold the next page is asked for, so the scroll never catches up with it. */
const PREFETCH_MARGIN = '300px 0px';

/** Grey squares where tiles are about to be: a whole first screen, or one more row. */
function TileSkeleton({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="aspect-square animate-pulse bg-muted" data-testid="tile-skeleton" />
      ))}
    </>
  );
}

/**
 * A profile's full-width grid of photos — the sends, the climbs put up — a
 * page at a time, the next one fetched as the bottom comes into view.
 */
export default function PagedTileGrid<T>({
  fetchPage: fetchItems,
  renderTile,
  itemKey,
  emptyText,
  errorText,
  testId,
  className = '',
}: PagedTileGridProps<T>) {
  const { toast } = useToast();
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // One request in flight at a time: the sentinel can fire more than once while
  // a page is still on its way.
  const inFlight = useRef(false);
  // Bumped whenever the grid starts over, so a page that comes back for the
  // previous profile is dropped rather than shown under the wrong name.
  const epoch = useRef(0);

  const fetchPage = useCallback(async (next: number) => {
    if (inFlight.current) return;
    inFlight.current = true;
    const mine = epoch.current;
    if (next === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      const response = await fetchItems(next);
      if (epoch.current !== mine) return;
      setItems((prev) => (next === 1 ? response.items : [...prev, ...response.items]));
      setPage(next);
      setHasMore(response.hasMore);
    } catch (error) {
      if (epoch.current !== mine) return;
      console.error('Error loading tiles:', error);
      toast({ title: errorText, variant: 'destructive' });
      // Otherwise the sentinel, still in view, would ask again at once.
      setHasMore(false);
    } finally {
      // A request from before the grid started over has already been let go
      // of — the reset below cleared the flag for the new one, which may be on
      // its way right now.
      if (epoch.current === mine) {
        inFlight.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchItems]); // toast and errorText are stable

  // The first page, afresh whenever the grid starts over.
  useEffect(() => {
    epoch.current += 1;
    inFlight.current = false;
    setItems([]);
    setPage(0);
    setHasMore(true);
    void fetchPage(1);
  }, [fetchPage]);

  // The next page as the bottom of the grid comes into view. Rebuilt after
  // every page on purpose: an observer only reports *changes*, and on a screen
  // tall enough that the sentinel is still in view once a page has landed, a
  // kept observer would never speak again. A fresh one reports the state as it
  // stands — so a tall screen fills itself, and a short one waits for the scroll.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) void fetchPage(page + 1);
      },
      { rootMargin: PREFETCH_MARGIN }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, page, fetchPage]);

  if (loading) {
    return (
      <div className={`${GRID} ${className}`} data-testid={`${testId}-loading`}>
        <TileSkeleton count={FIRST_SKELETON} />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={`py-8 text-center text-muted-foreground ${className}`}>
        {emptyText}
      </div>
    );
  }

  return (
    <div className={className} data-testid={testId}>
      <div className={GRID}>
        {items.map((item) => (
          <Fragment key={itemKey(item)}>{renderTile(item)}</Fragment>
        ))}
        {loadingMore && <TileSkeleton count={MORE_SKELETON} />}
      </div>
      {hasMore && <div ref={sentinelRef} className="h-px" data-testid={`${testId}-sentinel`} />}
    </div>
  );
}
